import { Loader2 } from 'lucide-react';
import type React from 'react';
import { useState } from 'react';
import { trpc } from '../../lib/trpc';
import { Button } from '../../ui/button';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '../../ui/dialog';
import { Input } from '../../ui/input';
import { Label } from '../../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../ui/select';

// Radix Select forbids an empty-string item value, so the "no type" choice uses
// this sentinel and is mapped back to `undefined` (→ null on submit) on change.
const NO_TYPE_VALUE = 'none';

export interface CreateContactDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Fired after the contact is created — callers typically navigate to its detail page. */
  onCreated: (contactId: number) => void;
}

/**
 * Minimal quick-create: Name, Type, and Owner only. Everything else (company,
 * emails/phones, address, notes, tags, custom fields) is filled in on the
 * contact's own detail page right after creation — matches how most CRMs
 * separate "quick add" from the full profile edit.
 */
export function CreateContactDialog({ open, onOpenChange, onCreated }: CreateContactDialogProps) {
  const [displayName, setDisplayName] = useState('');
  const [typeId, setTypeId] = useState<number | undefined>(undefined);
  const [ownerId, setOwnerId] = useState<number | undefined>(undefined);
  const [error, setError] = useState<string | null>(null);

  const { data: contactTypesData } = trpc.contactTypes.list.useQuery();
  const contactTypes = contactTypesData?.items || [];
  const { data: members = [] } = trpc.workspace.listMembers.useQuery();
  const { data: me } = trpc.auth.me.useQuery();
  const utils = trpc.useUtils();

  // Reset the form whenever the dialog transitions to open. Adjusting state
  // during render (React's documented pattern) rather than in an effect avoids
  // an extra cascading render.
  const [wasOpen, setWasOpen] = useState(open);
  if (open !== wasOpen) {
    setWasOpen(open);
    if (open) {
      setDisplayName('');
      setTypeId(undefined);
      setOwnerId(me?.uid);
      setError(null);
    }
  }

  const createMutation = trpc.contacts.create.useMutation({
    onSuccess: (contact) => {
      utils.contacts.search.invalidate();
      onOpenChange(false);
      onCreated(contact.id);
    },
    onError: (err) => setError(err.message),
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const trimmedName = displayName.trim();
    if (!trimmedName) {
      setError('Name is required');
      return;
    }
    if (!ownerId) {
      setError('Owner is required');
      return;
    }
    setError(null);
    createMutation.mutate({
      label: trimmedName,
      displayName: trimmedName,
      // Type is optional — a blank selection creates a genuinely typeless contact.
      typeId: typeId ?? null,
      ownerId,
    });
  };

  return (
    <Dialog open={open} onOpenChange={(next) => !createMutation.isPending && onOpenChange(next)}>
      <DialogContent className="sm:max-w-[420px]">
        <DialogHeader>
          <DialogTitle>Create Contact</DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-1.5">
            <Label htmlFor="create-contact-name" className="text-xs font-semibold">
              Name
            </Label>
            <Input
              id="create-contact-name"
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
              placeholder="e.g. John Doe / Acme Corp"
              autoFocus
            />
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="create-contact-type" className="text-xs font-semibold">
              Type <span className="font-normal text-muted-foreground">(optional)</span>
            </Label>
            <Select
              value={typeId?.toString() ?? NO_TYPE_VALUE}
              onValueChange={(val) => setTypeId(val === NO_TYPE_VALUE ? undefined : Number(val))}
            >
              <SelectTrigger id="create-contact-type" className="w-full">
                <SelectValue placeholder="No type" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={NO_TYPE_VALUE}>No type</SelectItem>
                {contactTypes.map((type) => (
                  <SelectItem key={type.id} value={type.id.toString()}>
                    {type.label}
                    {type.isDefault ? ' · Default' : ''}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="create-contact-owner" className="text-xs font-semibold">
              Owner
            </Label>
            <Select value={ownerId?.toString()} onValueChange={(val) => setOwnerId(Number(val))}>
              <SelectTrigger id="create-contact-owner" className="w-full">
                <SelectValue placeholder="Select an owner..." />
              </SelectTrigger>
              <SelectContent>
                {members.map((member) => (
                  <SelectItem key={member.id} value={member.id.toString()}>
                    {member.fullName || member.email}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {error && <p className="text-xs text-destructive">{error}</p>}

          <DialogFooter className="pt-2">
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={createMutation.isPending}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={createMutation.isPending} className="gap-1.5">
              {createMutation.isPending && <Loader2 className="h-4 w-4 animate-spin" />}
              {createMutation.isPending ? 'Creating...' : 'Create Contact'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
